"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/axios";

export type CustomerRow = {
  id: number;
  name: string;
  phone: string;
  email?: string | null;
  dob?: string | null;
  gstin?: string | null;
  outstanding_balance?: number;
  loyalty_points?: number;
  loyalty_tier?: string;
};

export function useCustomersList(params: {
  search: string;
  tier: string;
  outstandingOnly: boolean;
}) {
  const q = new URLSearchParams();
  if (params.search) q.set("search", params.search);
  if (params.tier) q.set("tier", params.tier);
  if (params.outstandingOnly) q.set("outstanding", "1");
  q.set("limit", "80");

  return useQuery({
    queryKey: ["customers", params],
    queryFn: async () => {
      const { data } = await api.get<{ customers: CustomerRow[] }>(
        `/api/customers?${q.toString()}`,
      );
      return data.customers;
    },
  });
}

export function useCustomerBundle(id: number) {
  return useQuery({
    queryKey: ["customer", id, "bundle"],
    enabled: id > 0,
    queryFn: async () => {
      const [cust, ledger, loyalty, contacts] = await Promise.all([
        api.get<{ customer: CustomerRow }>(`/api/customers/${id}`),
        api.get<{ customer: CustomerRow; ledger: LedgerEntry[] }>(
          `/api/customers/${id}/ledger`,
        ),
        api.get<{
          customer: CustomerRow;
          config: Record<string, unknown>;
          transactions: LoyaltyTx[];
          tier_benefits: string[];
        }>(`/api/customers/${id}/loyalty`),
        api.get<{ contacts: ContactRow[] }>(`/api/customers/${id}/contacts`),
      ]);
      return {
        customer: cust.data.customer,
        ledger: ledger.data.ledger,
        loyalty: loyalty.data,
        contacts: contacts.data.contacts,
      };
    },
  });
}

export type LedgerEntry = {
  type: string;
  ref: string;
  ref_id: number;
  detail: string;
  debit: number;
  credit: number;
  points: number | null;
  running_balance: number;
};

export type LoyaltyTx = {
  id: number;
  transaction_type: string;
  points: number;
  created_at: string;
  notes?: string | null;
};

export type ContactRow = {
  id: number;
  notes: string | null;
  contact_date: string;
};

export function useCustomerPayment(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      amount: number;
      payment_mode?: string;
      reference_no?: string;
      notes?: string;
    }) => {
      const { data } = await api.post(`/api/customers/${id}/payments`, body);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["customer", id], exact: false });
    },
  });
}

export function useCustomerContactNote(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (notes: string) => {
      await api.post(`/api/customers/${id}/contacts`, { notes });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["customer", id], exact: false });
    },
  });
}
